<template>
  <Dialog
    :is-open="isOpen"
    title="AI Summary Settings"
    width="500px"
    @close="$emit('close')"
  >
    <div :class="$style.container">
      <div :class="$style.section">
        <h3 :class="$style.sectionTitle">Gemini API Configuration</h3>
        <p :class="$style.description">
          To use AI-powered summaries, you need a Gemini API key from Google AI
          Studio.
        </p>

        <div :class="$style.apiKeySection">
          <label :class="$style.label" for="apiKey">API Key</label>
          <div :class="$style.inputGroup">
            <input
              id="apiKey"
              v-model="apiKey"
              type="password"
              :class="$style.input"
              placeholder="Enter your Gemini API key"
              @keyup.enter="saveApiKey"
            />
            <button
              :class="$style.toggleButton"
              @click="toggleApiKeyVisibility"
              type="button"
            >
              {{ showApiKey ? "Hide" : "Show" }}
            </button>
          </div>
        </div>

        <div :class="$style.actions">
          <button :class="$style.saveButton" @click="saveApiKey">
            Save API Key
          </button>
          <button :class="$style.testButton" @click="testApiKey">
            Test Connection
          </button>
        </div>

        <!-- Test Results -->
        <div v-if="testResult" :class="$style.testResult">
          <div
            :class="[
              $style.testStatus,
              testResult.success ? $style.success : $style.error,
            ]"
          >
            <CheckCircleIcon v-if="testResult.success" :size="20" />
            <WarningIcon v-else :size="20" />
            {{ testResult.message }}
          </div>
        </div>
      </div>

      <div :class="$style.section">
        <h3 :class="$style.sectionTitle">How to Get an API Key</h3>
        <ol :class="$style.instructions">
          <li>
            Visit
            <a
              href="https://aistudio.google.com/app/apikey"
              target="_blank"
              rel="noopener noreferrer"
              :class="$style.link"
            >
              Google AI Studio
            </a>
          </li>
          <li>Sign in with your Google account (free)</li>
          <li>Click "Create API Key"</li>
          <li>Copy the generated API key</li>
          <li>Paste it in the field above</li>
        </ol>
      </div>

      <div :class="$style.section">
        <h3 :class="$style.sectionTitle">Privacy & Security</h3>
        <ul :class="$style.privacyList">
          <li>Your API key is stored locally in your browser</li>
          <li>We never send your API key to our servers</li>
          <li>
            Only your aggregated time tracking data is sent to Google for
            analysis
          </li>
          <li>You can delete your API key at any time</li>
        </ul>
      </div>

      <div :class="$style.section">
        <button :class="$style.deleteButton" @click="deleteApiKey">
          Delete API Key
        </button>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import Dialog from "./Dialog.vue";
import { CheckCircleIcon, WarningIcon } from "./icons";

defineProps<{
  isOpen: boolean;
}>();

defineEmits<{
  (e: "close"): void;
}>();

const apiKey = ref("");
const showApiKey = ref(false);
const testResult = ref<{ success: boolean; message: string } | null>(null);

onMounted(() => {
  // Load existing API key
  const storedKey = localStorage.getItem("gemini-api-key");
  if (storedKey) {
    apiKey.value = storedKey;
  }
});

const toggleApiKeyVisibility = () => {
  showApiKey.value = !showApiKey.value;
  const input = document.getElementById("apiKey") as HTMLInputElement;
  if (input) {
    input.type = showApiKey.value ? "text" : "password";
  }
};

const saveApiKey = () => {
  if (!apiKey.value.trim()) {
    testResult.value = {
      success: false,
      message: "Please enter an API key",
    };
    return;
  }

  localStorage.setItem("gemini-api-key", apiKey.value.trim());
  testResult.value = {
    success: true,
    message: "API key saved successfully!",
  };

  // Clear test result after 3 seconds
  setTimeout(() => {
    testResult.value = null;
  }, 3000);
};

const testApiKey = async () => {
  if (!apiKey.value.trim()) {
    testResult.value = {
      success: false,
      message: "Please enter an API key first",
    };
    return;
  }

  testResult.value = {
    success: false,
    message: "Testing connection...",
  };

  try {
    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash-exp:generateContent?key=${apiKey.value.trim()}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: "Hello, this is a test message.",
                },
              ],
            },
          ],
          generationConfig: {
            maxOutputTokens: 10,
            temperature: 0.1,
          },
        }),
      }
    );

    if (response.ok) {
      testResult.value = {
        success: true,
        message: "Connection successful! Your API key is working.",
      };
    } else {
      const errorData = await response.json();
      testResult.value = {
        success: false,
        message: `Connection failed: ${
          errorData.error?.message || "Unknown error"
        }`,
      };
    }
  } catch (error) {
    testResult.value = {
      success: false,
      message: `Connection failed: ${
        error instanceof Error ? error.message : "Network error"
      }`,
    };
  }

  // Clear test result after 5 seconds
  setTimeout(() => {
    testResult.value = null;
  }, 5000);
};

const deleteApiKey = () => {
  if (
    confirm(
      "Are you sure you want to delete your API key? This will disable AI summaries."
    )
  ) {
    localStorage.removeItem("gemini-api-key");
    apiKey.value = "";
    testResult.value = {
      success: true,
      message: "API key deleted successfully",
    };
  }
};
</script>

<style module>
.container {
  padding: 20px;
}

.section {
  margin-bottom: 24px;
}

.sectionTitle {
  margin: 0 0 12px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
}

.description {
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
  margin: 0 0 16px 0;
}

.apiKeySection {
  margin-bottom: 16px;
}

.label {
  display: block;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

.inputGroup {
  display: flex;
  gap: 8px;
}

.input {
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 10px 12px;
  color: white;
  font-size: 14px;
}

.input:focus {
  outline: none;
  border-color: #74b9ff;
  box-shadow: 0 0 0 2px rgba(116, 185, 255, 0.2);
}

.input::placeholder {
  color: rgba(255, 255, 255, 0.5);
}

.toggleButton {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 10px 12px;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  font-size: 14px;
  white-space: nowrap;
}

.toggleButton:hover {
  background: rgba(255, 255, 255, 0.15);
}

.actions {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.saveButton,
.testButton {
  padding: 10px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.saveButton {
  background: #74b9ff;
  color: white;
}

.saveButton:hover {
  background: #5a9bd8;
}

.testButton {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.testButton:hover {
  background: rgba(255, 255, 255, 0.15);
}

.testResult {
  margin-top: 12px;
}

.testStatus {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 4px;
  font-size: 14px;
}

.testStatus.success {
  background: rgba(0, 184, 148, 0.1);
  border: 1px solid rgba(0, 184, 148, 0.3);
  color: #00b894;
}

.testStatus.error {
  background: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.3);
  color: #e74c3c;
}

.instructions {
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin: 0;
  padding-left: 20px;
}

.instructions li {
  margin-bottom: 8px;
}

.link {
  color: #74b9ff;
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.privacyList {
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin: 0;
  padding-left: 20px;
}

.privacyList li {
  margin-bottom: 6px;
}

.deleteButton {
  background: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.3);
  color: #e74c3c;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.deleteButton:hover {
  background: rgba(231, 76, 60, 0.2);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .container {
    padding: 15px;
  }

  .actions {
    flex-direction: column;
  }

  .inputGroup {
    flex-direction: column;
  }

  .toggleButton {
    align-self: flex-start;
  }
}
</style>
